import { FC, ReactNode, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, SxProps, IconButton, Modal, Paper, Typography, Fade, Tooltip, Alert } from '@mui/material';
import { Info as InfoIcon, Close as CloseIcon } from '@mui/icons-material';

import { Breadcrumbs } from '@carcareers/components';

interface DashboardPageContainerProps {
  children: ReactNode;
  idReplacement?: string;
  nestedIdReplacement?: string;
  hideBreadcrumbs?: boolean;
  sx?: SxProps;
  loading?: boolean;
  helpContent?: ReactNode;
  helpTitle?: string;
}

export const DashboardPageContainer: FC<DashboardPageContainerProps> = ({
  children,
  idReplacement,
  nestedIdReplacement,
  hideBreadcrumbs,
  sx,
  loading,
  helpContent,
  helpTitle = 'Help',
}) => {
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const location = useLocation();

  const handleHelpToggle = () => {
    setIsHelpModalOpen((prev) => !prev);
  };

  const handleCloseModal = () => {
    setIsHelpModalOpen(false);
  };

  const shouldShowHelp = Boolean(helpContent);
  const isEmployerRoute = location.pathname.startsWith('/employer');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 64px)',
        width: '100%',
        gap: 2,
        p: 2,
        position: 'relative',
        ...sx,
      }}
    >
      {isEmployerRoute && (
        <Alert
          severity="info"
          sx={{
            width: '100%',
            fontWeight: 500,
            borderRadius: 3,
          }}
        >
          Need Help? Call{' '}
          <a href="tel:888-397-9797" style={{ color: 'inherit', textDecoration: 'underline' }}>
            (888) 397-9797
          </a>
        </Alert>
      )}

      {!hideBreadcrumbs && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {shouldShowHelp && (
            <Tooltip title="Help" arrow>
              <IconButton
                onClick={handleHelpToggle}
                sx={{
                  backgroundColor: 'background.paper',
                  borderRadius: 3,
                  px: 2,
                  py: 1,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
                aria-label="Show help information"
              >
                <InfoIcon color="primary" />
              </IconButton>
            </Tooltip>
          )}
          <Breadcrumbs
            loading={loading}
            idReplacement={idReplacement}
            nestedIdReplacement={nestedIdReplacement}
            sx={{
              px: 2,
              py: 1,
              backgroundColor: 'background.paper',
              borderRadius: 3,
              flex: 1,
            }}
          />
        </Box>
      )}

      {hideBreadcrumbs && shouldShowHelp && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Tooltip title="Help" arrow>
            <IconButton
              onClick={handleHelpToggle}
              sx={{
                backgroundColor: 'background.paper',
                borderRadius: 3,
                px: 2,
                py: 1,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              aria-label="Show help information"
            >
              <InfoIcon color="primary" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {children}

      {shouldShowHelp && (
        <Modal
          open={isHelpModalOpen}
          onClose={handleCloseModal}
          closeAfterTransition
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Fade in={isHelpModalOpen}>
            <Paper
              elevation={8}
              sx={{
                maxWidth: 600,
                width: '100%',
                maxHeight: '80vh',
                overflow: 'auto',
                position: 'relative',
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 3,
                  pb: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="h6" component="h2">
                  {helpTitle}
                </Typography>
                <IconButton onClick={handleCloseModal} size="small" aria-label="Close help modal">
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box sx={{ p: 3 }}>{helpContent}</Box>
            </Paper>
          </Fade>
        </Modal>
      )}
    </Box>
  );
};
